import { useState, useCallback, useMemo } from 'react';
import { DealerProfileInfo } from '../../models/osb-model/osb-dealer-info';
import { ServiceInfo } from '../../models/osb-model/osb-dealerservice-info';
import {
    OSB_SERVICE_TYPE,
    SERVICE_DELIVERY_TYPE,
    SERVICE_FLOW,
} from '../../components/sections/owners-osb/osb-constant';
import { VehicleData } from '../../models/osb-model/osb-vehicle-step';
import ServiceHandler from '../../services/service-handler';
import {
    useHttp,
    useOSBStep,
    useServiceStep,
    useVehicleStep,
} from '../owners-osb';
import { useAllOSBState } from '../owners-osb/use-all-osb-state';
import { OSBContentData } from '../../models/osb-model/osb-content-data';
import { SCHEDULED_SERVICE } from '../../components/sections/osb-v3/osb-v3-constant';

export const useDealerServiceWithOfflineRetry = () => {
    const { httpState, dispatch } = useHttp();
    const { osbVehicleStep } = useVehicleStep();
    const { osbServiceStep } = useServiceStep();
    const { osbStep } = useOSBStep();
    const [mainServices, setMainServices] = useState<ServiceInfo[]>([]);
    const [deliveryServices, setDeliveryServices] = useState<ServiceInfo[]>([]);
    const [additionalServices, setAdditionalServices] = useState<ServiceInfo[]>(
        []
    );
    const bookable = useAllOSBState();

    const vehicleData: VehicleData = useMemo(() => {
        const details = osbVehicleStep.vehicleDetails;
        if (details?.vin) {
            return {
                vin: details.vin,
                registrationNumber: details.registrationNumber,
                mileage: details.mileageInKm ?? '',
            };
        }
        return {
            modelName: osbVehicleStep.defaultModelName,
            buildYear: osbVehicleStep.defaultBuildYear,
            mileage: osbVehicleStep.defaultMileage,
        } as VehicleData;
    }, [osbVehicleStep.vehicleDetails]);

    const vouchers = osbServiceStep.voucherCode
        ? [osbServiceStep.voucherCode]
        : [];

    const getRequestSource = () => {
        return osbStep.source || 'Web';
    };

    const handleMainServiceRetry = useCallback(
        async (
            initialMainServices: ServiceInfo[],
            dealer: DealerProfileInfo,
            dealerServiceContent: OSBContentData
        ) => {
            const isAlreadyValid = !initialMainServices.some(
                service => service.serviceId.toString() === SCHEDULED_SERVICE
            );

            if (isAlreadyValid) {
                console.log(
                    'Main services are already valid. No retry needed.'
                );
                return;
            }

            console.log(
                'Main services are incomplete. Starting offline retry...'
            );

            const maxRetryCount =
                dealerServiceContent?.serviceApiRetryAttempts || 1;
            const retryInterval = 5 * 1000;

            for (
                let retryCount = 1;
                retryCount <= Number(maxRetryCount);
                retryCount++
            ) {
                await new Promise(r => setTimeout(r, retryInterval));
                console.log(`Performing offline retry ${retryCount}...`);

                try {
                    const result = await ServiceHandler.OsbDealerServices.getServicesList(
                        {
                            dealerCode: dealer.dealerCode,
                            vehicleData: vehicleData,
                            vouchers: vouchers,
                            serviceDeliveryType: SERVICE_DELIVERY_TYPE.WORKSHOP,
                            requestSourceType: getRequestSource(),
                            bookable: bookable,
                            DSLAPIVersion: osbStep.DSLAPIVersion,
                        }
                    );

                    const allApiAdditionalServices =
                        result.value.dealerServices.additionalServices;

                    const newMainServices = result.value.dealerServices.mainServices.filter(
                        service =>
                            service.additionalInfo?.serviceType?.toUpperCase() ===
                                OSB_SERVICE_TYPE.SERVICES.toUpperCase() &&
                            service.serviceId.toString() !==
                                SERVICE_FLOW.GA_SERVICE_ID
                    );

                    const newDeliveryServices = allApiAdditionalServices.filter(
                        service => {
                            const type = service.additionalInfo?.serviceType?.toUpperCase();
                            return (
                                type ===
                                    OSB_SERVICE_TYPE.DELIVERY.toUpperCase() ||
                                type === 'DROPOFF'
                            );
                        }
                    );

                    const newAdditionalServices = allApiAdditionalServices.filter(
                        service => {
                            const type = service.additionalInfo?.serviceType?.toUpperCase();
                            return (
                                type !==
                                    OSB_SERVICE_TYPE.DELIVERY.toUpperCase() &&
                                type !== 'DROPOFF'
                            );
                        }
                    );

                    const isNowValid = !newMainServices.some(
                        service =>
                            service.serviceId.toString() === SCHEDULED_SERVICE
                    );

                    if (isNowValid || retryCount === maxRetryCount) {
                        console.log(
                            'Offline retry successful or max retries reached. Updating all services.'
                        );
                        setMainServices(newMainServices);
                        setDeliveryServices(newDeliveryServices);
                        setAdditionalServices(newAdditionalServices);
                        return;
                    }
                } catch (error) {
                    console.error(`Offline retry ${retryCount} failed:`, error);
                    if (retryCount === maxRetryCount) {
                        console.error('Max offline retries failed.');
                        return;
                    }
                }
            }
        },
        [bookable, osbStep, vehicleData]
    );

    const loadInitialServicesAndRetryMains = useCallback(
        async (
            dealer: DealerProfileInfo,
            dealerServiceContent: OSBContentData
        ) => {
            dispatch({ type: 'REQUEST' });

            const source = getRequestSource();
            bookable.dealerCode = dealer.dealerCode;
            bookable.source = source;

            try {
                const result = await ServiceHandler.OsbDealerServices.getServicesList(
                    {
                        dealerCode: dealer.dealerCode,
                        vehicleData: vehicleData,
                        serviceDeliveryType: SERVICE_DELIVERY_TYPE.WORKSHOP,
                        requestSourceType: source,
                        bookable: bookable,
                        DSLAPIVersion: osbStep.DSLAPIVersion,
                    }
                );

                const allApiAdditionalServices =
                    result.value.dealerServices.additionalServices;

                const delivery = allApiAdditionalServices.filter(service => {
                    const type = service.additionalInfo?.serviceType?.toUpperCase();
                    return (
                        type === OSB_SERVICE_TYPE.DELIVERY.toUpperCase() ||
                        type === 'DROPOFF'
                    );
                });

                const additional = allApiAdditionalServices.filter(service => {
                    const type = service.additionalInfo?.serviceType?.toUpperCase();
                    return (
                        type !== OSB_SERVICE_TYPE.DELIVERY.toUpperCase() &&
                        type !== 'DROPOFF'
                    );
                });
                const initialMainServices = result.value.dealerServices.mainServices.filter(
                    service =>
                        service.additionalInfo?.serviceType?.toUpperCase() ===
                            OSB_SERVICE_TYPE.SERVICES.toUpperCase() &&
                        service.serviceId.toString() !==
                            SERVICE_FLOW.GA_SERVICE_ID
                );

                setDeliveryServices(delivery);
                setAdditionalServices(additional);
                setMainServices(initialMainServices);

                dispatch({ type: 'RESPONSE' });

                handleMainServiceRetry(
                    initialMainServices,
                    dealer,
                    dealerServiceContent
                );
            } catch (error) {
                let finalErrorMessage = dealerServiceContent?.apiErrorMessage;
                if (error instanceof Error) {
                    finalErrorMessage = finalErrorMessage + ' ' + error.message;
                } else {
                    finalErrorMessage = 'An unexpected error occurred.';
                }
                dispatch({
                    type: 'ERROR',
                    error: finalErrorMessage,
                });
            }
        },
        [dispatch, bookable, osbStep, vehicleData, handleMainServiceRetry]
    );

    return {
        httpState,
        mainServices,
        deliveryServices,
        additionalServices,
        loadInitialServicesAndRetryMains,
    };
};
